import { Directory, Filesystem } from '@capacitor/filesystem';

import { compartir } from './compartir';
import { esApp } from './plataforma';

export interface FicheroADescargar {
	nombre: string;
	/** El contenido, en base64, como lo devuelve la API. */
	base64: string;
	/** Tipo MIME. */
	tipo: string;
	/**
	 * Solo para la web: escribir el contenido como texto y no byte a byte. Es lo que hacía la
	 * descarga de la plantilla de salarios, y se conserva para no cambiar el fichero que sale.
	 */
	comoTexto?: boolean;
}

const aBytes = (binario: string): Uint8Array<ArrayBuffer> =>
	Uint8Array.from(binario, (caracter) => caracter.charCodeAt(0));

const descargarEnElNavegador = ({ nombre, base64, tipo, comoTexto }: FicheroADescargar): void => {
	const binario = atob(base64);
	const blob = new Blob([comoTexto ? binario : aBytes(binario)], { type: tipo });
	const url = window.URL.createObjectURL(blob);
	const enlace = document.createElement('a');
	enlace.href = url;
	enlace.download = nombre;
	document.body.appendChild(enlace);
	enlace.click();
	document.body.removeChild(enlace);
	window.URL.revokeObjectURL(url);
};

const abrirEnLaApp = async ({
	nombre,
	base64
}: Pick<FicheroADescargar, 'nombre' | 'base64'>): Promise<void> => {
	const { uri } = await Filesystem.writeFile({
		path: nombre,
		data: base64,
		directory: Directory.Cache
	});

	try {
		await compartir({ title: nombre, url: uri });
	} catch {
		// Cerrar el menú sin elegir nada rechaza la promesa. El fichero ya está guardado: no es un fallo.
	}
};

/**
 * Entregar al usuario un fichero que llega de la API.
 *
 * En el navegador es la descarga de siempre: un enlace temporal con `download`. Dentro de la app no
 * hay gestor de descargas y ese enlace no hace nada, así que el fichero se guarda en la caché de la
 * app y se abre el menú del sistema, desde donde se puede guardar en Archivos, abrir o enviar.
 */
export const descargarFichero = async (fichero: FicheroADescargar): Promise<void> => {
	if (esApp()) {
		await abrirEnLaApp(fichero);
		return;
	}

	descargarEnElNavegador(fichero);
};

export interface FicheroEstatico {
	/** Ruta dentro de `public/`, con la barra inicial. */
	ruta: string;
	nombre: string;
}

const aBase64 = (bytes: Uint8Array): string =>
	btoa(bytes.reduce((binario, byte) => binario + String.fromCharCode(byte), ''));

/**
 * Entregar al usuario un fichero que viaja con la aplicación (una plantilla de `public/`).
 *
 * En el navegador es un enlace directo con `download`, como se hacía. Dentro de la app ese enlace no
 * hace nada: se lee el fichero —va dentro del binario, no hay red— y sigue el mismo camino que
 * cualquier otra descarga.
 */
export const descargarFicheroEstatico = async ({
	ruta,
	nombre
}: FicheroEstatico): Promise<void> => {
	if (!esApp()) {
		const enlace = document.createElement('a');
		enlace.href = ruta;
		enlace.download = nombre;
		enlace.click();
		return;
	}

	const respuesta = await fetch(ruta);
	if (!respuesta.ok) {
		throw new Error(`No se pudo leer ${ruta} (${respuesta.status})`);
	}

	const base64 = aBase64(new Uint8Array(await respuesta.arrayBuffer()));
	await abrirEnLaApp({ nombre, base64 });
};
